import { useTranslation } from 'react-i18next';

const WHATSAPP_ENTERPRISE =
  'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Quiero%20una%20propuesta%20personalizada%20para%20mi%20operaci%C3%B3n.';

const ACCENT = '#a855f7';
const ACCENT_DARK = '#7c3aed';

const SPECS = [
  { icon: 'ri-shield-check-line',        labelKey: 'ent_spec_sla_l',          valueKey: 'ent_spec_sla_v' },
  { icon: 'ri-customer-service-2-line',  labelKey: 'ent_spec_channel_l',      valueKey: 'ent_spec_channel_v' },
  { icon: 'ri-plug-line',                labelKey: 'ent_spec_integrations_l', valueKey: 'ent_spec_integrations_v' },
  { icon: 'ri-code-s-slash-line',        labelKey: 'ent_spec_dev_l',          valueKey: 'ent_spec_dev_v' },
];

const CHECKLIST = [
  'ent_check_1', 'ent_check_2', 'ent_check_3', 'ent_check_4', 'ent_check_5', 'ent_check_6',
  'ent_check_7', 'ent_check_8', 'ent_check_9', 'ent_check_10', 'ent_check_11',
];

export default function EnterpriseSection() {
  const { t } = useTranslation();

  return (
    <section
      id="enterprise"
      className="py-14 md:py-24 px-4 sm:px-5 md:px-6"
      style={{ background: 'linear-gradient(180deg, #020817 0%, #090620 55%, #020817 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        <div
          className="relative rounded-3xl overflow-hidden border"
          style={{
            borderColor: ACCENT + '4d',
            background: 'linear-gradient(135deg, rgba(22,11,48,0.96) 0%, rgba(8,12,34,0.98) 62%)',
            boxShadow: `0 0 60px ${ACCENT}22, 0 24px 60px rgba(0,0,0,0.5)`,
          }}
        >
          {/* Decorative glows */}
          <div
            className="absolute -top-24 -right-16 w-80 h-80 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${ACCENT}2e 0%, transparent 70%)` }}
          />
          <div
            className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${ACCENT_DARK}26 0%, transparent 70%)` }}
          />
          {/* Top accent line */}
          <div
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{ background: `linear-gradient(to right, transparent, ${ACCENT}, transparent)` }}
          />

          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 p-6 sm:p-8 md:p-12">
            {/* ── Left: pitch + specs + CTA ─────────────────────── */}
            <div>
              <div
                className="inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full font-body mb-4 border"
                style={{ backgroundColor: ACCENT + '22', borderColor: ACCENT + '55', color: ACCENT }}
              >
                <i className="ri-vip-crown-2-line text-xs"></i>
                {t('ent_badge')}
              </div>

              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-3" style={{ color: '#ffffff' }}>
                {t('ent_title')}
              </h2>
              <p className="font-body text-sm md:text-base leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.62)' }}>
                {t('ent_sub')}
              </p>

              {/* Price */}
              <div className="flex flex-wrap items-end gap-x-4 gap-y-1 mb-6">
                <div>
                  <span className="font-body text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.40)' }}>
                    {t('ent_price_label')}
                  </span>
                  <div className="font-heading text-4xl md:text-5xl font-extrabold leading-none mt-1" style={{ color: ACCENT }}>
                    {t('ent_price_value')}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 pb-1">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: ACCENT }}>
                    <i className="ri-add-circle-line text-sm"></i>
                  </div>
                  <span className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {t('ent_impl')}{' '}
                    <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{t('ent_impl_value')}</strong>
                  </span>
                </div>
              </div>

              {/* Specs grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                {SPECS.map((spec) => (
                  <div
                    key={spec.labelKey}
                    className="flex items-start gap-3 rounded-xl px-3.5 py-3"
                    style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: ACCENT + '1f', color: ACCENT }}>
                      <i className={`${spec.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="font-body text-[11px] leading-tight" style={{ color: 'rgba(255,255,255,0.45)' }}>
                        {t(spec.labelKey)}
                      </p>
                      <p className="font-body text-sm font-bold leading-snug mt-0.5" style={{ color: '#ffffff' }}>
                        {t(spec.valueKey)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href={WHATSAPP_ENTERPRISE}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center justify-center gap-2 font-bold text-sm md:text-base py-3.5 px-7 rounded-xl font-body transition-all duration-200 cursor-pointer whitespace-nowrap transform hover:scale-[1.03] active:scale-[0.97] w-full sm:w-auto"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DARK})`,
                  color: '#ffffff',
                  boxShadow: `0 8px 24px ${ACCENT}40`,
                }}
              >
                <i className="ri-whatsapp-line text-base"></i>
                {t('ent_cta')}
              </a>
            </div>

            {/* ── Right: checklist card ─────────────────────────── */}
            <div
              className="rounded-2xl border p-5 sm:p-6 self-start"
              style={{ borderColor: ACCENT + '33', background: 'rgba(255,255,255,0.035)' }}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: ACCENT + '22', color: ACCENT }}>
                  <i className="ri-clipboard-line text-lg"></i>
                </div>
                <h3 className="font-heading text-base md:text-lg font-bold" style={{ color: '#ffffff' }}>
                  {t('ent_checklist_title')}
                </h3>
              </div>

              <ul className="flex flex-col gap-2.5">
                {CHECKLIST.map((key) => (
                  <li key={key} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-[2px]" style={{ color: ACCENT }}>
                      <i className="ri-checkbox-circle-fill text-sm"></i>
                    </div>
                    <span className="font-body text-[13px] leading-snug" style={{ color: 'rgba(255,255,255,0.78)' }}>
                      {t(key)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
